import React, { FunctionComponent, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { v1 as uuid } from "uuid";
import useCustomizationContext from "../../../../../hooks/useCustomizationContext";
import { ReactComponent as IconCheckFilled } from "../../../../../icons/check-filled.svg";
import { ReactComponent as IconType } from "../../../../../icons/16x16/type.svg";
import { Panel } from "../../../../../states/customization";
import { findCoverTitlePlacement } from "../../../../../utils/customization/cover";
import { getVariableByEnv } from "../../../../../utils/variableByEnv";
import EditableBookTitle from "../../../../molecules/customization/EditableBookTitle";
import PanelClosing from "../../../../molecules/customization/PanelClosing";
import OptionList from "../../../../molecules/customization/panels/OptionList";
import PanelValidation from "../../../../molecules/customization/panels/PanelValidation";
import "./style.scss";

const TitlePanel: FunctionComponent = () => {
  const {
    configuration,
    content,
    dispatch,
    template,
    undoRedo,
    ui: { defaultFontKey },
  } = useCustomizationContext();
  const intl = useIntl();
  const [panelUndoKey] = useState<string>(`coverPanel${uuid()}`);
  const [initialTitle] = useState<string>(
    content.templateType === "book" ? content.bookTitle || "" : ""
  );

  const showFontChoice = getVariableByEnv("REACT_APP_COVER_TEXT_CHOICE") === "true";

  const handleClosePanel = () => {
    const newTitle = content.templateType === "book" ? content.bookTitle || "" : "";
    if (undoRedo.lastUndoKey === panelUndoKey) {
      dispatch({ type: "undoRedo", way: "undo" });
    } else if (initialTitle !== newTitle) {
      dispatch({
        bookTitle: initialTitle,
        options: configuration?.options,
        type: "changeBookTitle",
        undoKey: "changeBookTitle",
      });
    }

    dispatch({ type: "setPanel", panel: Panel.CLOSED });
  };

  const handleValidation = () => {
    dispatch({ type: "setPanel", panel: Panel.CLOSED });
  };

  const titlePlaces = findCoverTitlePlacement(template.pageDefinitions, content.pages);
  const bookTitleFontKey = content.templateType === "book" ? content.bookTitleFontKey : undefined;

  const textItems = useMemo(() => {
    if (
      !showFontChoice ||
      content.templateType !== "book" ||
      (!titlePlaces.cover && !titlePlaces.spine && !titlePlaces.frontMatter)
    ) {
      return null;
    }

    const fontDefinitionsFormatted = template.fontDefinitions.map((fontDefinition) => {
      return {
        key: fontDefinition.key,
        label: fontDefinition.fontTag,
        onSelect: () => {
          dispatch({
            bookTitleFontKey: fontDefinition.key,
            type: "changeBookTitleFontKey",
            undoKey: panelUndoKey,
          });
        },
        value: fontDefinition.fontTag,
      };
    });

    if (fontDefinitionsFormatted.length > 1) {
      const selectedFont = bookTitleFontKey;

      return (
        <OptionList
          icon={<IconType />}
          optionItems={fontDefinitionsFormatted}
          selectedValue={selectedFont}
          title={intl.formatMessage({ id: "kustom_OptionsPanel_type" })}
          type="text"
        />
      );
    }
  }, [bookTitleFontKey, defaultFontKey, dispatch, intl, template.fontDefinitions, panelUndoKey]);

  return (
    <div className="TitlePanel">
      <PanelClosing onClick={handleClosePanel}>
        <FormattedMessage id="kustom_TitlePanel_title" />
      </PanelClosing>
      <div className="TitlePanel-content">
        <EditableBookTitle undoKey={panelUndoKey} />

        {showFontChoice && textItems}

        <section className="TitlePanel-content-places">
          <h3 className="TitlePanel-content-places-title">
            {intl.formatMessage({
              id: "kustom_CoverPanel_bookTitle_places_title",
            })}
          </h3>
          <div className="TitlePanel-content-places-info">
            {intl.formatMessage({
              id: "kustom_CoverPanel_bookTitle_places_details",
            })}
          </div>
          <ul className="TitlePanel-content-places-list">
            {titlePlaces.cover && (
              <li className="TitlePanel-content-places-list-item">
                <IconCheckFilled />
                {intl.formatMessage({
                  id: "kustom_CoverPanel_bookTitle_places_cover",
                })}
              </li>
            )}
            {titlePlaces.spine && (
              <li className="TitlePanel-content-places-list-item">
                <IconCheckFilled />
                {intl.formatMessage({
                  id: "kustom_CoverPanel_bookTitle_places_spine",
                })}
              </li>
            )}
            {titlePlaces.frontMatter && (
              <li className="TitlePanel-content-places-list-item">
                <IconCheckFilled />
                {intl.formatMessage({
                  id: "kustom_CoverPanel_bookTitle_places_flyleaf",
                })}
              </li>
            )}
          </ul>
        </section>
      </div>
      <div className="TitlePanel-footer">
        <PanelValidation onCancel={handleClosePanel} onValidate={handleValidation} />
      </div>
    </div>
  );
};

export default TitlePanel;
